import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDeal } from '../hooks/useDeal';
import { useDeals } from '../hooks/useDeals';
import { useContacts } from '../hooks/useContacts';
import { useActivities } from '../hooks/useActivities';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { ToastContainer } from '../components/common/Toast';
import DealModal from '../components/deals/DealModal';
import { openMeetingForDeal } from '../utils/googleCalendarUrl';
import { formatCurrency } from '../utils/formatters';

const DealDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get('id');
  const toast = useToast();

  // Load deal data
  const deal = useDeal(dealId);
  const { updateDeal, deleteDeal } = useDeals();
  const { contacts } = useContacts();
  // Now using unified dealActivities collection (includes emails, calendar events, notes, etc)
  const { activities, addActivity, loading: activitiesLoading } = useActivities(dealId);

  const [newActivity, setNewActivity] = useState({
    type: 'note',
    title: '',
    content: ''
  });
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    date: '',
    time: '',
    duration: 30,
    addGoogleMeet: true,
    description: ''
  });

  // Get contacts associated with this deal
  const dealContacts = useMemo(() => {
    if (!deal?.contactIds || !contacts) return [];
    return contacts.filter(contact => deal.contactIds.includes(contact.id));
  }, [deal, contacts]);

  if (!deal) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" message="Loading deal..." />
      </div>
    );
  }

  const getStageColor = (stage) => {
    const colors = {
      'Contacts': 'bg-gray-100 dark:bg-gray-700/30 text-gray-700 dark:text-gray-300 ring-gray-500/10',
      'Proposal': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-blue-700/10',
      'Negotiation': 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 ring-amber-700/10',
      'Won': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-700/10',
      'Lost': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-red-700/10'
    };
    return colors[stage] || colors['Contacts'];
  };

  const getActivityIcon = (type) => {
    const icons = {
      email: { icon: 'mail', color: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800' },
      call: { icon: 'call', color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800' },
      note: { icon: 'note', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800' },
      meeting: { icon: 'event', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800' },
      calendar_event: { icon: 'calendar_month', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800' },
      task: { icon: 'task_alt', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800' }
    };
    return icons[type] || icons.note;
  };

  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 7) {
      return date.toLocaleDateString('en-US');
    } else if (diffDays > 0) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffMins > 0) {
      return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    } else {
      return 'Just now';
    }
  };

  const handleAddActivity = async () => {
    if (!newActivity.title.trim() || !newActivity.content.trim()) {
      toast.error('Titolo e contenuto sono obbligatori');
      return;
    }

    setIsAddingActivity(true);
    try {
      await addActivity({
        dealId: dealId,
        type: newActivity.type,
        title: newActivity.title,
        content: newActivity.content
      });

      setNewActivity({ type: 'note', title: '', content: '' });
      toast.success('Attività aggiunta con successo');
    } catch (error) {
      toast.error('Errore nell\'aggiunta dell\'attività');
    } finally {
      setIsAddingActivity(false);
    }
  };

  const handleEditDeal = async (formData) => {
    setModalLoading(true);
    try {
      await updateDeal(dealId, formData);
      setIsEditModalOpen(false);
      toast.success('Deal aggiornato con successo');
    } catch (error) {
      console.error('Error updating deal:', error);
      toast.error('Errore nell\'aggiornamento del deal');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteDeal = async () => {
    setIsDeleting(true);
    try {
      await deleteDeal(dealId);
      toast.success('Deal eliminato con successo');
      navigate('/pipeline');
    } catch (error) {
      console.error('Error deleting deal:', error);
      toast.error('Errore nell\'eliminazione del deal');
      setIsDeleting(false);
    }
  };

  const handleScheduleMeeting = () => {
    if (!meetingForm.date || !meetingForm.time) {
      toast.error('Data e ora sono obbligatori');
      return;
    }

    const startDate = new Date(`${meetingForm.date}T${meetingForm.time}`);

    // Get attendee emails from deal contacts
    const attendeeEmails = dealContacts
      .filter(c => c.email)
      .map(c => c.email);

    // Open Google Calendar with pre-filled data
    openMeetingForDeal(deal, {
      startDate,
      durationMinutes: meetingForm.duration,
      description: meetingForm.description,
      attendeeEmails,
    });

    // Close modal and reset form
    setShowMeetingModal(false);
    setMeetingForm({
      date: '',
      time: '',
      duration: 30,
      addGoogleMeet: true,
      description: ''
    });

    toast.success('Google Calendar aperto - completa la creazione del meeting');
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-background-light dark:bg-background-dark relative">
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />

      {/* Top Header Area */}
      <div className="shrink-0 px-6 py-5 border-b border-border-dark/50 bg-background-light dark:bg-background-dark z-10">
        <div className="max-w-[1200px] mx-auto w-full flex flex-col gap-4">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="flex">
            <ol className="flex items-center space-x-2">
              <li>
                <button
                  onClick={() => navigate('/pipeline')}
                  className="text-slate-500 dark:text-text-secondary hover:text-primary text-sm font-medium transition-colors"
                >
                  Pipeline
                </button>
              </li>
              <li><span className="text-slate-500 dark:text-text-secondary text-sm">/</span></li>
              <li><span className="text-slate-900 dark:text-white text-sm font-medium">{deal.title}</span></li>
            </ol>
          </nav>

          {/* Deal Header */}
          <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-border-dark p-6 shadow-sm">
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <h1 className="text-slate-900 dark:text-white text-2xl font-bold leading-tight mb-2">{deal.title}</h1>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStageColor(deal.stage)}`}>
                      {deal.stage}
                    </span>
                    {deal.category && (
                      <span className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-700/30 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 ring-1 ring-inset ring-gray-500/10">
                        {deal.category}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <div className="text-right">
                    <div className="text-slate-500 dark:text-text-secondary text-sm mb-1">Valore</div>
                    <div className="text-slate-900 dark:text-white text-3xl font-bold">{formatCurrency(deal.value)}</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowMeetingModal(true)}
                      className="px-3 py-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 border border-purple-500 dark:border-purple-600 hover:border-purple-600 dark:hover:border-purple-500 rounded-lg transition-colors flex items-center gap-1.5"
                    >
                      <span className="material-symbols-outlined text-[16px]">event</span>
                      Schedule Meeting
                    </button>
                    <button
                      onClick={() => setIsEditModalOpen(true)}
                      className="px-3 py-1.5 text-sm font-medium text-primary hover:text-blue-600 border border-primary hover:border-blue-600 rounded-lg transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="px-3 py-1.5 text-sm font-medium text-red-500 hover:text-red-600 border border-red-500 hover:border-red-600 rounded-lg transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              {/* Associated Contacts */}
              {dealContacts.length > 0 && (
                <div className="border-t border-slate-200 dark:border-border-dark pt-4">
                  <div className="text-slate-500 dark:text-text-secondary text-xs font-medium uppercase tracking-wider mb-2">
                    Contatti Associati
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {dealContacts.map(contact => (
                      <button
                        key={contact.id}
                        onClick={() => navigate(`/contact-details?id=${contact.id}`)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-[#233648] hover:bg-slate-200 dark:hover:bg-[#2c445a] rounded-lg transition-colors"
                      >
                        <span className="material-symbols-outlined text-[16px] text-slate-600 dark:text-white">person</span>
                        <span className="text-sm text-slate-900 dark:text-white font-medium">{contact.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
        <div className="max-w-[1200px] mx-auto w-full">
          <div className="flex flex-col gap-6">
            {/* Add Activity Form */}
            <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-border-dark shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 dark:border-border-dark">
                <h3 className="text-slate-900 dark:text-white font-bold text-base">Aggiungi Attività</h3>
              </div>
              <div className="p-5">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2">
                    {['note', 'call', 'email', 'meeting'].map(type => (
                      <button
                        key={type}
                        onClick={() => setNewActivity(prev => ({ ...prev, type }))}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          newActivity.type === type
                            ? 'bg-primary text-white'
                            : 'bg-slate-100 dark:bg-[#233648] text-slate-700 dark:text-white hover:bg-slate-200 dark:hover:bg-[#2c445a]'
                        }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={newActivity.title}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Titolo attività..."
                    className="w-full bg-slate-50 dark:bg-[#192633] border border-slate-200 dark:border-border-dark rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-text-secondary focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  />
                  <textarea
                    value={newActivity.content}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Descrizione..."
                    rows="3"
                    className="w-full bg-slate-50 dark:bg-[#192633] border border-slate-200 dark:border-border-dark rounded-lg p-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-text-secondary focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                  ></textarea>
                  <div className="flex justify-end">
                    <button
                      onClick={handleAddActivity}
                      disabled={isAddingActivity}
                      className="bg-primary hover:bg-blue-600 text-white text-sm font-bold py-2 px-4 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                    >
                      {isAddingActivity ? 'Salvataggio...' : 'Salva Attività'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Unified Activities Timeline (emails, calendar events, notes, calls, etc) */}
            <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-border-dark shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 dark:border-border-dark flex items-center justify-between">
                <h3 className="text-slate-900 dark:text-white font-bold text-base flex items-center gap-2">
                  <span className="material-symbols-outlined text-[20px]">history</span>
                  Timeline Attività
                  {activities.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 rounded-full">
                      {activities.length}
                    </span>
                  )}
                </h3>
              </div>
              <div className="p-5">
                {activitiesLoading ? (
                  <div className="text-center py-12">
                    <LoadingSpinner size="md" message="Loading activities..." />
                  </div>
                ) : activities.length === 0 ? (
                  <div className="text-center py-12">
                    <span className="material-symbols-outlined text-slate-300 dark:text-slate-600 text-[48px] mb-3 block">history</span>
                    <p className="text-slate-500 dark:text-text-secondary text-sm">Nessuna attività registrata per questo deal</p>
                    <p className="text-slate-400 dark:text-slate-600 text-xs mt-1">Aggiungi note o collega email/eventi dal Chrome extension</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-6">
                    {activities.map((activity, index) => {
                      const activityStyle = getActivityIcon(activity.type);
                      const metadata = activity.metadata || {};

                      return (
                        <div key={activity.id} className="flex gap-4 group">
                          <div className="flex flex-col items-center">
                            <div className={`size-8 rounded-full flex items-center justify-center border ${activityStyle.color} shrink-0`}>
                              <span className="material-symbols-outlined text-[16px]">{activityStyle.icon}</span>
                            </div>
                            {index < activities.length - 1 && (
                              <div className="w-px h-full bg-slate-200 dark:bg-border-dark my-2"></div>
                            )}
                          </div>
                          <div className="flex-1 pb-4">
                            <div className="flex justify-between items-start mb-1">
                              <h4 className="text-sm font-bold text-slate-900 dark:text-white">{activity.title}</h4>
                              <span className="text-xs text-slate-500 dark:text-text-secondary whitespace-nowrap ml-4">
                                {activity.timestamp ? formatRelativeTime(activity.timestamp) : ''}
                              </span>
                            </div>

                            {/* Activity content/description */}
                            {activity.content && (
                              <p className="text-sm text-slate-600 dark:text-text-secondary leading-relaxed whitespace-pre-wrap mb-2">
                                {activity.content}
                              </p>
                            )}

                            {/* Email-specific info */}
                            {activity.type === 'email' && metadata.contactEmail && (
                              <p className="text-slate-600 dark:text-text-secondary text-xs mb-1">
                                <span className="material-symbols-outlined text-[12px] inline-block mr-1 align-middle">person</span>
                                {metadata.contactEmail}
                              </p>
                            )}

                            {/* Calendar event-specific info */}
                            {activity.type === 'calendar_event' && metadata.attendees?.length > 0 && (
                              <p className="text-slate-600 dark:text-text-secondary text-xs mb-1">
                                <span className="material-symbols-outlined text-[12px] inline-block mr-1 align-middle">group</span>
                                {metadata.attendees.map(a => a.displayName || a.email).join(', ')}
                              </p>
                            )}

                            {/* Action links */}
                            <div className="flex items-center gap-3 mt-2">
                              {/* Email: View in Gmail */}
                              {activity.type === 'email' && metadata.gmailThreadId && (
                                <a
                                  href={`https://mail.google.com/mail/u/0/#all/${metadata.gmailThreadId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:text-blue-600 text-xs font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <span className="material-symbols-outlined text-[14px]">open_in_new</span>
                                  View in Gmail
                                </a>
                              )}

                              {/* Calendar: View in Calendar */}
                              {activity.type === 'calendar_event' && metadata.googleEventLink && (
                                <a
                                  href={metadata.googleEventLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:text-blue-600 text-xs font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <span className="material-symbols-outlined text-[14px]">open_in_new</span>
                                  View in Calendar
                                </a>
                              )}

                              {/* Google Meet link */}
                              {activity.type === 'calendar_event' && metadata.googleMeetLink && (
                                <a
                                  href={metadata.googleMeetLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-green-600 hover:text-green-700 text-xs font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <span className="material-symbols-outlined text-[14px]">videocam</span>
                                  Join Meet
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Deal Modal */}
      <DealModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        deal={deal}
        onSubmit={handleEditDeal}
        loading={modalLoading}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Elimina Deal</h3>
            <p className="text-slate-600 dark:text-text-secondary mb-6">
              Sei sicuro di voler eliminare <strong>{deal?.title}</strong>? Questa azione non può essere annullata.
              {activities.length > 0 && (
                <span className="block mt-2 text-amber-600 dark:text-amber-400">
                  Attenzione: Questo deal ha {activities.length} attività associate che verranno eliminate.
                </span>
              )}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-white bg-white dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] rounded-lg hover:bg-gray-50 dark:hover:bg-[#233648] transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteDeal}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <span className="material-symbols-outlined text-[18px] animate-spin">refresh</span>
                    <span>Eliminazione...</span>
                  </>
                ) : (
                  <span>Elimina Deal</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Meeting Modal */}
      {showMeetingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="size-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <span className="material-symbols-outlined text-purple-600 dark:text-purple-400">event</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Schedule Meeting</h3>
                <p className="text-sm text-slate-500 dark:text-text-secondary">for {deal?.title}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-text-secondary mb-1">
                    Data
                  </label>
                  <input
                    type="date"
                    value={meetingForm.date}
                    onChange={(e) => setMeetingForm(prev => ({ ...prev, date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full bg-slate-50 dark:bg-[#192633] border border-slate-200 dark:border-border-dark rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-text-secondary mb-1">
                    Ora
                  </label>
                  <input
                    type="time"
                    value={meetingForm.time}
                    onChange={(e) => setMeetingForm(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full bg-slate-50 dark:bg-[#192633] border border-slate-200 dark:border-border-dark rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-text-secondary mb-1">
                  Durata
                </label>
                <select
                  value={meetingForm.duration}
                  onChange={(e) => setMeetingForm(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                  className="w-full bg-slate-50 dark:bg-[#192633] border border-slate-200 dark:border-border-dark rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                >
                  <option value={15}>15 minuti</option>
                  <option value={30}>30 minuti</option>
                  <option value={45}>45 minuti</option>
                  <option value={60}>1 ora</option>
                  <option value={90}>1 ora 30 minuti</option>
                  <option value={120}>2 ore</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-text-secondary mb-1">
                  Note (opzionale)
                </label>
                <textarea
                  value={meetingForm.description}
                  onChange={(e) => setMeetingForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Aggiungi note o agenda..."
                  rows="2"
                  className="w-full bg-slate-50 dark:bg-[#192633] border border-slate-200 dark:border-border-dark rounded-lg p-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-text-secondary focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none"
                ></textarea>
              </div>

              {dealContacts.length > 0 && (
                <div className="bg-slate-50 dark:bg-[#192633] rounded-lg p-3 border border-slate-200 dark:border-border-dark">
                  <p className="text-xs text-slate-500 dark:text-text-secondary mb-2">
                    Inviti saranno inviati a:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {dealContacts.map(contact => (
                      <span
                        key={contact.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-[#233648] text-xs text-slate-700 dark:text-white rounded-md border border-slate-200 dark:border-border-dark"
                      >
                        <span className="material-symbols-outlined text-[12px]">person</span>
                        {contact.email || contact.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowMeetingModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-white bg-white dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] rounded-lg hover:bg-gray-50 dark:hover:bg-[#233648] transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleScheduleMeeting}
                disabled={!meetingForm.date || !meetingForm.time}
                className="px-4 py-2 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                <span>Apri Google Calendar</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealDetails;
